import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { useToast } from '@/shared/widgets/toast';
import type { CustomizationService } from '../../application/services/CustomizationService';
import type { CustomizationSelection } from '../../domain/models/CustomizationSelection';
import type { ParsedCustomizationOptions } from '../../domain/models/CustomizationOptions';
import type { ProductCustomization } from '../../domain/models/ProductCustomization';
import type { SizeOption } from '../../utils/parseSizeList';
import {
  parseCustomizationOptions,
  parseGoldPuritiesForMetal,
  resolveActiveMetalColor,
  metalToSelectionColor,
} from '../../utils/parseCustomizationOptions';
import { findSizeOption, normalizeSizeKey } from '../../utils/parseSizeList';

export interface CustomizationSheetContext {
  designId: string;
  designName: string;
  customizationOptions: unknown;
  metal: string;
  goldPurity: string | null;
  diamondStoneType: string | null;
  solitaireCarat: number | null;
  solitaireClarity: string | null;
  solitaireColor: string | null;
  customizationId: number;
  size: string | null;
  discountedPrice: number | null;
}

export interface UseCustomizationSheetOptions {
  service: CustomizationService;
  context: CustomizationSheetContext | null;
  selectedSize: SizeOption | null;
  isOpen: boolean;
  onClose: () => void;
}

export interface UseCustomizationSheetReturn {
  draft: CustomizationSelection;
  parsedOptions: ParsedCustomizationOptions;
  goldPurities: ParsedCustomizationOptions['goldPurities'];
  previewPrice: number | null;
  isPreviewLoading: boolean;
  isPriceAnimating: boolean;
  isApplying: boolean;
  handleOptionChange: (key: string, value: string | number) => void;
  handleSelectAndApply: (
    key: string,
    value: string | number
  ) => Promise<{ customization: ProductCustomization; selectedSize: SizeOption | null } | null>;
  handleApply: () => Promise<{ customization: ProductCustomization; selectedSize: SizeOption | null } | null>;
  handleCancel: () => void;
}

function buildInitialDraft(
  context: CustomizationSheetContext,
  selectedSize: SizeOption | null,
  customizationOptions: unknown
): CustomizationSelection {
  const parsed = parseCustomizationOptions(customizationOptions);
  const sizes = parsed.sizes;
  
  const rawSize = selectedSize?.key ?? (context.size ? String(context.size) : undefined);
  const normalizedRawSize = normalizeSizeKey(rawSize);
  const matchedSize = sizes.find((s) => normalizeSizeKey(s.key) === normalizedRawSize);
  const sizeKey = matchedSize?.key ?? sizes[0]?.key;

  return {
    customization_id: context.customizationId,
    metal_color: metalToSelectionColor(context.metal),
    metal_karatage: context.goldPurity ?? undefined,
    diamond_type: parsed.isSolitaire ? undefined : (context.diamondStoneType ?? undefined),
    solitaire_carat: context.solitaireCarat ?? undefined,
    solitaire_clarity: context.solitaireClarity ?? undefined,
    solitaire_color: context.solitaireColor ?? undefined,
    solitaire_quality:
      context.solitaireClarity && context.solitaireColor
        ? `${context.solitaireClarity}_${context.solitaireColor}`
        : undefined,
    size: sizeKey,
  };
}

function buildNextDraft(
  prev: CustomizationSelection,
  key: string,
  value: string | number,
  customizationOptions: unknown
): CustomizationSelection {
  let next: CustomizationSelection = { ...prev, [key]: value };

  if (key === 'metal_color') {
    const purities = parseGoldPuritiesForMetal(customizationOptions, String(value));
    const currentPurityValid = purities.some((p) => p.metalPurity === prev.metal_karatage);
    if (!currentPurityValid && purities.length > 0) {
      next = {
        ...next,
        metal_karatage: purities[purities.length - 1].metalPurity,
      };
    }
  }

  if (key === 'solitaire_quality') {
    const quality = String(value).split('_');
    next = {
      ...next,
      solitaire_quality: String(value),
      solitaire_clarity: quality[0],
      solitaire_color: quality[1],
    };
  }

  return next;
}

function getCustomizationErrorMessage(error: unknown): string {
  if (error && typeof error === 'object') {
    const err = error as { data?: { message?: string }; message?: string };
    if (typeof err.data?.message === 'string' && err.data.message) return err.data.message;
    if (typeof err.message === 'string' && err.message) return err.message;
  }
  return 'Unable to update customization. Please try again.';
}

export function useCustomizationSheet({
  service,
  context,
  selectedSize,
  isOpen,
  onClose,
}: UseCustomizationSheetOptions): UseCustomizationSheetReturn {
  const { toast } = useToast();
  const [draft, setDraft] = useState<CustomizationSelection>({});
  const [previewPrice, setPreviewPrice] = useState<number | null>(null);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [isPriceAnimating, setIsPriceAnimating] = useState(false);
  const [isApplying, setIsApplying] = useState(false);
  const appliedSnapshot = useRef<CustomizationSelection>({});
  const lastSuccessfulDraftRef = useRef<CustomizationSelection>({});
  const previewRequestId = useRef(0);
  const wasOpenRef = useRef(false);
  const previousPreviewPriceRef = useRef<number | null>(null);
  const priceAnimationTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const parsedOptions = useMemo(
    () => parseCustomizationOptions(context?.customizationOptions ?? null),
    [context?.customizationOptions]
  );

  const goldPurities = useMemo(() => {
    if (!context?.customizationOptions || !draft.metal_color) return [];
    return parseGoldPuritiesForMetal(context.customizationOptions, draft.metal_color);
  }, [context?.customizationOptions, draft.metal_color]);

  const resetDraft = useCallback(() => {
    if (!context) return;
    const initial = buildInitialDraft(context, selectedSize, context.customizationOptions);
    appliedSnapshot.current = initial;
    lastSuccessfulDraftRef.current = initial;
    setDraft(initial);
    setPreviewPrice(context.discountedPrice);
    previousPreviewPriceRef.current = context.discountedPrice;
    setIsPriceAnimating(false);
    if (priceAnimationTimeoutRef.current) {
      clearTimeout(priceAnimationTimeoutRef.current);
      priceAnimationTimeoutRef.current = null;
    }
  }, [context, selectedSize]);

  const revertToLastSuccessfulDraft = useCallback(() => {
    const lastDraft = lastSuccessfulDraftRef.current;
    setDraft({ ...lastDraft });
    setPreviewPrice(previousPreviewPriceRef.current);
    setIsPriceAnimating(false);
    if (priceAnimationTimeoutRef.current) {
      clearTimeout(priceAnimationTimeoutRef.current);
      priceAnimationTimeoutRef.current = null;
    }
  }, []);

  const showCustomizationError = useCallback(
    (error: unknown) => {
      toast({
        title: 'Customization unavailable',
        description: getCustomizationErrorMessage(error),
        variant: 'destructive',
        duration: 3000,
      });
    },
    [toast]
  );

  useEffect(() => {
    if (isOpen && !wasOpenRef.current && context) {
      resetDraft();
    }
    wasOpenRef.current = isOpen;
  }, [isOpen, context, resetDraft]);

  useEffect(() => {
    return () => {
      if (priceAnimationTimeoutRef.current) {
        clearTimeout(priceAnimationTimeoutRef.current);
      }
    };
  }, []);

  const fetchPreview = useCallback(
    async (nextDraft: CustomizationSelection) => {
      if (!context) return;
      const requestId = ++previewRequestId.current;
      setIsPreviewLoading(true);
      try {
        const result = await service.previewCustomization(context.designId, nextDraft);
        if (requestId !== previewRequestId.current) return;
        const newPrice = result.discountedPrice ?? result.price;
        const prevPrice = previousPreviewPriceRef.current;
        if (prevPrice != null && newPrice != null && prevPrice !== newPrice) {
          setIsPriceAnimating(true);
          if (priceAnimationTimeoutRef.current) {
            clearTimeout(priceAnimationTimeoutRef.current);
          }
          priceAnimationTimeoutRef.current = setTimeout(() => {
            setIsPriceAnimating(false);
            priceAnimationTimeoutRef.current = null;
          }, 500);
        }
        previousPreviewPriceRef.current = newPrice;
        setPreviewPrice(newPrice);
        lastSuccessfulDraftRef.current = nextDraft;
      } catch (error) {
        if (requestId !== previewRequestId.current) return;
        revertToLastSuccessfulDraft();
        showCustomizationError(error);
      } finally {
        if (requestId === previewRequestId.current) {
          setIsPreviewLoading(false);
        }
      }
    },
    [context, service, revertToLastSuccessfulDraft, showCustomizationError]
  );

  const handleOptionChange = useCallback(
    (key: string, value: string | number) => {
      if (!context) return;

      setDraft((prev) => {
        const next = buildNextDraft(prev, key, value, context.customizationOptions);
        void fetchPreview(next);
        return next;
      });
    },
    [context, fetchPreview]
  );

  const handleSelectAndApply = useCallback(
    async (key: string, value: string | number) => {
      if (!context || isApplying) return null;

      const next = buildNextDraft(draft, key, value, context.customizationOptions);
      setDraft(next);
      setIsApplying(true);

      try {
        await fetchPreview(next);
        const result = await service.applyCustomization(context.designId, next);
        const selected = findSizeOption(parsedOptions.sizes, next.size ?? null);
        appliedSnapshot.current = next;
        return { customization: result, selectedSize: selected };
      } catch (error) {
        revertToLastSuccessfulDraft();
        showCustomizationError(error);
        return null;
      } finally {
        setIsApplying(false);
      }
    },
    [
      context,
      draft,
      fetchPreview,
      isApplying,
      parsedOptions.sizes,
      revertToLastSuccessfulDraft,
      service,
      showCustomizationError,
    ]
  );

  const handleApply = useCallback(async () => {
    if (!context) return null;
    setIsApplying(true);
    try {
      const result = await service.applyCustomization(context.designId, draft);
      const selected = findSizeOption(parsedOptions.sizes, draft.size ?? null);
      appliedSnapshot.current = draft;
      onClose();
      return { customization: result, selectedSize: selected };
    } catch (error) {
      revertToLastSuccessfulDraft();
      showCustomizationError(error);
      return null;
    } finally {
      setIsApplying(false);
    }
  }, [context, draft, onClose, parsedOptions.sizes, revertToLastSuccessfulDraft, service, showCustomizationError]);

  const handleCancel = useCallback(() => {
    setDraft(appliedSnapshot.current);
    setPreviewPrice(context?.discountedPrice ?? null);
    onClose();
  }, [context?.discountedPrice, onClose]);

  return {
    draft,
    parsedOptions,
    goldPurities,
    previewPrice,
    isPreviewLoading,
    isPriceAnimating,
    isApplying,
    handleOptionChange,
    handleSelectAndApply,
    handleApply,
    handleCancel,
  };
}

export { resolveActiveMetalColor };
